<!-- RightArea.vue — v3 -->
<!-- Abre NoteCardModal automaticamente se noteStore.pendingOpenNoteId estiver preenchido. -->
<!-- Comportamento original 100% preservado quando pendingOpenNoteId é null. -->

<script setup>
import { storeToRefs } from "pinia";
import { onMounted, ref, computed } from "vue";
import NoteSection from "./Note/NoteSection.vue";
import TextDisplayer from "./Displayer/TextDisplayer.vue";
import NoteCardModal from "./Note/NoteCardModal.vue";
import { useRouter } from "vue-router";
import { useNoteStore } from "@/stores/useNoteStore";
import { useTextStore } from "@/stores/useTextStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useUserStore } from "@/stores/useUserStore";

const router = useRouter();
const textStore = useTextStore();
const noteStore = useNoteStore();
const activityStore = useActivityStore();
const userStore = useUserStore();

const { selectedText } = storeToRefs(textStore);

// >>> NOVO: modal aberto via link compartilhado
const showSharedNoteModal = ref(false);
const sharedNoteId = ref(null);

const currentUserId = computed(() => userStore.currentUser?.userId ?? null);

onMounted(async () => {
  // Acesso direto à rota (refresh de página) sem contexto nas stores: não há
  // texto/série para exibir. Volta para a lista de textos em vez de travar em
  // "Carregando...".
  if (!textStore.idText || !activityStore.idActivity) {
    router.replace({ name: "TextForm" });
    return;
  }

  // Carregamento original — inalterado
  if (textStore.idText) {
    await textStore.getTextById(textStore.idText);
  }

  // >>> NOVO: se NotePublicView deixou um noteId pendente, abre o modal
  // e limpa imediatamente para não reabrir em navegações futuras.
  if (noteStore.pendingOpenNoteId) {
    sharedNoteId.value = noteStore.pendingOpenNoteId;
    noteStore.pendingOpenNoteId = null; // consome o valor — uso único
    showSharedNoteModal.value = true;
  }
});

function handleSharedNoteModalClose() {
  showSharedNoteModal.value = false;
  sharedNoteId.value = null;
}
</script>

<template>
  <main class="main-content">
    <TextDisplayer
      v-if="selectedText && activityStore.selectedActivity"
      :key="activityStore.idActivity"
      :textCreatedByUserId="textStore.selectedText?.createdByUserId"
      :textUpdatedByUserId="textStore.selectedText?.updatedByUserId"
      :notes="noteStore.allNotes"
    />
    <div v-else class="right-area-placeholder">Carregando...</div>
  </main>

  <aside class="sidebar-right">
    <NoteSection :key="activityStore.idActivity" />
  </aside>

  <!-- Modal da nota compartilhada -->
  <Teleport to="body">
    <NoteCardModal
      v-if="showSharedNoteModal && sharedNoteId && currentUserId"
      :show="showSharedNoteModal"
      :noteId="sharedNoteId"
      :userId="currentUserId"
      @close="handleSharedNoteModalClose"
    />
  </Teleport>
</template>

<style scoped>
.main-content {
  background-color: var(--bg-content);
  border-radius: 16px;
  padding-left: 0px;
  display: flex;
  flex-direction: column;
  /* Sem height: 100% — o grid do DashboardLayout estica este item
     (align-items: stretch) respeitando a margin-top e o padding-bottom do
     grid, preservando o respiro de 8px no rodapé. Um height:100% somado à
     margin-top:8px transbordaria 8px e colaria o TextDisplayer no fundo. */
  min-height: 0;
  justify-content: center;
  align-items: center;
}

.sidebar-right {
  background: var(--bg-header);
  border-radius: 16px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 0px;
  min-height: 0;
}
</style>